import {
  FormErrorMessage,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  FormControl,
  Input,
  Modal,
  TextLink,
} from '@time-line/components/common';
import type { NotificationSchema } from 'hooks/useNotifyBackInStock';
import {
  notificationFormSchema,
  useBackInStockNotification,
} from 'hooks/useNotifyBackInStock';
import { useSiteSpecificRoutes } from 'hooks/useSiteSpecificRoutes';
import { env } from 'lib/constants/env';
import NextLink from 'next/link';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface ProductOutOfStockButtonProps {
  productVariantId: string;
}

export function ProductOutOfStockButton({
  productVariantId,
}: ProductOutOfStockButtonProps) {
  const { data: session } = useSession();
  const toast = useToast();

  const [TERMS, PRIVACY_POLICY] = useSiteSpecificRoutes([
    'TERMS',
    'PRIVACY_POLICY',
  ]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Omit<NotificationSchema, 'variant'>>({
    resolver: zodResolver(notificationFormSchema),
  });

  const { mutate: notify, isPending, isError } = useBackInStockNotification();
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    if (session?.user?.email) {
      reset({ email: session?.user?.email });
    }
  }, [reset, session]);

  const onSubmit = handleSubmit(
    (formData: Omit<NotificationSchema, 'variant'>) => {
      const notifyData = {
        ...formData,
        variant: productVariantId,
      };

      notify(notifyData, {
        onSuccess: () => {
          onClose();
          toast({
            position: 'bottom',
            duration: 2000,
            isClosable: true,
            render: ({ onClose: onCloseAlert }) => (
              <Alert
                variant="solid"
                colorScheme="gray"
                isIconVisible={false}
                onClose={onCloseAlert}
                borderRadius={{ base: 'none', sm: 'base' }}
                margin={0}
              >
                Notification enabled.
              </Alert>
            ),
          });
        },
      });
    },
  );

  if (!env.NEXT_PUBLIC_OOS_NOTIFICATION_ENABLED) {
    return <Button isDisabled>Out of stock</Button>;
  }

  return (
    <>
      <Button className="button--out-of-stock" onClick={() => onOpen()}>
        Notify me when available
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} headerIconName="Supply">
        <Text as="h2" size="title" mb="2xs">
          Out of stock
        </Text>
        <Text mb="m" size="body2" color="blackAlpha.700">
          You'll get an email once this product comes back in stock.
        </Text>
        <FormControl
          errorMessage={errors.email?.message}
          isInvalid={!!errors.email}
        >
          <Input type="email" {...register('email')} placeholder="Email" />
        </FormControl>
        <Button
          type="submit"
          onClick={onSubmit}
          width="full"
          mt="sm"
          mb="3xs"
          isLoading={isPending}
        >
          Notify me
        </Button>
        {isError && (
          <FormControl isInvalid={isError}>
            <FormErrorMessage>Sorry, something went wrong</FormErrorMessage>
          </FormControl>
        )}
        <Text textAlign="center" size="detail" color="blackAlpha.700">
          By registering your email address, you are agreeing to our{' '}
          <TextLink
            as={NextLink}
            href={TERMS}
            variant="underline"
            textProps={{
              size: 'detail',
              color: 'blackAlpha.700',
            }}
          >
            terms and conditions
          </TextLink>{' '}
          and{' '}
          <TextLink
            as={NextLink}
            href={PRIVACY_POLICY}
            variant="underline"
            textProps={{
              size: 'detail',
              color: 'blackAlpha.700',
            }}
          >
            privacy policy
          </TextLink>
          .
        </Text>
      </Modal>
    </>
  );
}
